import { cn } from '../../cn/cn';

export type ButtonVariant = 'ghost' | 'outline' | 'solid';
export type ButtonSize = 'sm' | 'md' | 'lg';
export type ButtonColor =
  | 'primary'
  | 'secondary'
  | 'error'
  | 'warning'
  | 'info'
  | 'success';

/**
 * Colour is three custom properties, not a class per colour-variant pair, so a
 * new colour is one line and the variants never change.
 */
const COLORS: Record<ButtonColor, string> = {
  primary:
    '[--btn-main:var(--color-primary)] [--btn-dark:var(--color-primary-dark)] [--btn-contrast:var(--color-primary-contrast)]',
  secondary:
    '[--btn-main:var(--color-secondary)] [--btn-dark:var(--color-secondary-dark)] [--btn-contrast:var(--color-secondary-contrast)]',
  error:
    '[--btn-main:var(--color-error)] [--btn-dark:var(--color-error-dark)] [--btn-contrast:var(--color-error-contrast)]',
  warning:
    '[--btn-main:var(--color-warning)] [--btn-dark:var(--color-warning-dark)] [--btn-contrast:var(--color-warning-contrast)]',
  info: '[--btn-main:var(--color-info)] [--btn-dark:var(--color-info-dark)] [--btn-contrast:var(--color-info-contrast)]',
  success:
    '[--btn-main:var(--color-success)] [--btn-dark:var(--color-success-dark)] [--btn-contrast:var(--color-success-contrast)]',
};

/**
 * Up to `align-middle` resets a `<button>` explicitly, not via a base layer,
 * so it draws the same in all three apps whatever they import.
 *
 * Labels stay sentence case; nothing here uppercases them.
 *
 * `relative` anchors the loading indicator. The focus ring matches the fields,
 * so keyboard users see one shape everywhere.
 *
 * `no-underline` is for `asChild` anchors, which carry the browser underline.
 * It is set at rest and under `hover:`. The `asChild` anchors have no other
 * link styling, so this is what keeps them looking like buttons.
 */
const BASE =
  'relative inline-flex items-center justify-center gap-2 m-0 border-0' +
  ' border-solid bg-transparent appearance-none cursor-pointer align-middle' +
  ' box-border font-sans font-medium rounded-md select-none whitespace-nowrap' +
  ' transition-[background-color,box-shadow,border-color,color] duration-150' +
  ' no-underline hover:no-underline' +
  ' focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40' +
  ' disabled:cursor-default disabled:pointer-events-none' +
  ' disabled:text-disabled-foreground';

/**
 * Hides a loading button's label and icons but keeps their layout.
 *
 * `invisible`, not a transparent colour: a loading button is disabled, and a
 * colour would compete with the disabled grey. IconButton shares it.
 */
export const HIDDEN_WHILE_LOADING = 'invisible';

/** The spinner drawn over the middle of a loading button, in its own colour. */
export const BUTTON_LOADING_INDICATOR_CLASSES =
  'absolute left-1/2 -translate-x-1/2 flex';

/**
 * A solid button keeps one shadow in every state so it does not lurch under
 * the pointer; hover darkens the colour instead.
 */
const VARIANTS: Record<ButtonVariant, string> = {
  ghost: 'text-(--btn-main) hover:bg-(--btn-main)/8',
  outline:
    'border border-(--btn-main)/40 text-(--btn-main)' +
    ' hover:border-(--btn-main) hover:bg-(--btn-main)/8' +
    ' disabled:border-divider',
  solid:
    'bg-(--btn-main) text-(--btn-contrast) shadow-xs' +
    ' hover:bg-(--btn-dark)' +
    ' disabled:bg-selected disabled:shadow-none',
};

/**
 * A fixed height per size, not padding, so every variant of a size matches:
 * the outline border is drawn inside the box.
 */
const SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-sm',
  md: 'h-9 px-4 text-sm',
  lg: 'h-10 px-6 text-base',
};

/** The start and end icon slots: 8px of gap from the base, and the icon size. */
export const BUTTON_START_ICON_CLASSES: Record<ButtonSize, string> = {
  sm: 'inline-flex shrink-0 -ml-0.5 [&_svg]:size-4',
  md: 'inline-flex shrink-0 -ml-1 [&_svg]:size-4',
  lg: 'inline-flex shrink-0 -ml-1 [&_svg]:size-5',
};

export const BUTTON_END_ICON_CLASSES: Record<ButtonSize, string> = {
  sm: 'inline-flex shrink-0 -mr-0.5 [&_svg]:size-4',
  md: 'inline-flex shrink-0 -mr-1 [&_svg]:size-4',
  lg: 'inline-flex shrink-0 -mr-1 [&_svg]:size-5',
};

export const buttonClasses = ({
  variant,
  color,
  size,
  className,
}: {
  variant: ButtonVariant;
  color: ButtonColor;
  size: ButtonSize;
  className?: string;
}): string =>
  cn(BASE, COLORS[color], VARIANTS[variant], SIZES[size], className);
